import { num, type EarningsTotals } from "./requestsFormat";
import { minCashoutFor } from "./payinConfig";
import type { EarningsResponse, MinCashoutInfo } from "../types/orders";

export type MinCashoutLock = { currency: string; minimum: number; balance: number; waiverDays: number };

// Minimum-cashout facts off GET /orders/earnings (absent on an older server →
// the payinConfig mirror and the 80-day waiver).
export function minCashoutInfoFrom(payload: EarningsResponse): MinCashoutInfo {
  const mc = payload.minCashout;
  return {
    byCurrency: mc && typeof mc.byCurrency === "object" && mc.byCurrency ? mc.byCurrency : {},
    waiverDays: typeof mc?.waiverDays === "number" ? mc.waiverDays : 80,
    oldestUnsettledAt: typeof mc?.oldestUnsettledAt === "string" ? mc.oldestUnsettledAt : null,
    autoRelease: mc?.autoRelease === true,
  };
}

// Minimum-cashout lock: null = a transfer is allowed. Measured on the balance
// the talent SEES (founder 2026-09-20: the minimum "means the balance they see") — the
// gross of the currency holding the most, the Balance card's own number — not
// the net after our fee, which kept a card just over the line locked. Waiver:
// the oldest uncashed request is ≥ waiverDays old → money is never held hostage
// to the threshold. ONE rule for the Transfer modal, the Withdraw button and
// the Instant Payouts card; the server (lib/payouts/minCashout.js
// visibleBalanceForMin) stays the judge.
export function minCashoutLock({
  earningsByCur,
  minCashoutInfo,
}: {
  earningsByCur: EarningsTotals;
  minCashoutInfo: MinCashoutInfo;
}): MinCashoutLock | null {
  const entries = Object.entries(earningsByCur);
  if (entries.length === 0) return null;
  // Refer & Earn money in the balance counts toward the minimum, like the
  // server's (lib/payouts/cashout.js minRows).
  const seen = (v: EarningsTotals[string] | undefined) => num(v?.total) + num(v?.referral);
  const [cur, v] = entries.reduce((best, entry) =>
    seen(entry[1]) > seen(best[1]) ? entry : best
  );
  const total = seen(v);
  const minimum = num(minCashoutInfo.byCurrency[cur]) || minCashoutFor(cur);
  const oldest = minCashoutInfo.oldestUnsettledAt ? new Date(minCashoutInfo.oldestUnsettledAt).getTime() : NaN;
  const waived =
    Number.isFinite(oldest) && Date.now() - oldest >= minCashoutInfo.waiverDays * 24 * 60 * 60 * 1000;
  // Whole minor units, like the server (Math.round(total * 100)): a float sum of
  // 19.999999 must not lock a talent the server would let through.
  if (Math.round(total * 100) >= Math.round(minimum * 100) || waived) return null;
  return { currency: cur, minimum, balance: total, waiverDays: minCashoutInfo.waiverDays };
}
